import React from "react";
import { useTranslation } from "react-i18next";
import { Card, Row, Col } from "react-bootstrap";

interface DateInfoCardProps {
  requestedDate: string;
  scheduledDate: string;
  formatDate: (date: string) => string;
  stockType?: string;
  creationDate?: string;
}

const DateInfoCard: React.FC<DateInfoCardProps> = ({
  requestedDate,
  scheduledDate,
  formatDate,
  stockType,
  creationDate,
}) => {
  const { t } = useTranslation();
  
  // For drop stock type, hide scheduled date and use creation date as requested date
  const isDropStock = stockType === "drop";
  const displayDate = isDropStock && creationDate ? creationDate : requestedDate;
  const dateLabel = isDropStock ? t("inventoryDetails.creationDate") : t("inventoryDetails.requestedDate");
  return (
    <Card 
      className="mb-4 date-info-card"
      style={{
        borderRadius: "18px",
        background: "var(--bs-card-bg)",
        boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
        transition: "all 0.3s ease",
        overflow: "hidden",
        border: "1px solid var(--bs-border-color)"
      }}
    >
      <style>
        {`
          .date-info-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(0,0,0,0.12);
          }
          
          .date-icon {
            width: 50px;
            height: 50px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.3s ease;
          }
          
          .date-info-card:hover .date-icon {
            transform: scale(1.05);
          }
          
          .date-divider {
            width: 1px;
            background: rgba(0,0,0,0.08);
            height: 80%;
            position: absolute;
            top: 10%;
            left: 50%;
            display: none;
          }
          
          .date-label {
            font-size: 0.8rem;
            font-weight: 500;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            margin-bottom: 0.25rem;
            color: #6c757d;
          }
          
          .date-value {
            font-size: 1rem;
            font-weight: 600;
          }
          
          @media (min-width: 768px) {
            .date-divider {
              display: block;
            }
          }
        `}
      </style>
      
      <Card.Body className="py-4 px-4">
        <Row className="justify-content-between position-relative">
          
          <Col md={6} className="mb-3 mb-md-0">
            <div className="d-flex align-items-center">
              <div 
                className="date-icon bg-light bg-opacity-15 me-3"
                style={{
                  boxShadow: "0 4px 12px rgba(var(--bs-primary-rgb), 0.18)"
                }}
              >
                {/* Calendar SVG icon for requested date */}
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
              </div>
              
              <div>
                <div className="date-label">{dateLabel}</div>
                <div className="date-value text-primary">{formatDate(displayDate)}</div>
              </div>
            </div>
          </Col>
          
          {/* Only show scheduled date for non-drop stock types */}
          {!isDropStock && (
            <Col md={6}>
              <div className="d-flex align-items-center">
                <div 
                  className="date-icon bg-light bg-opacity-15 me-3"
                  style={{
                    boxShadow: "0 4px 12px rgba(var(--bs-success-rgb), 0.18)"
                  }}
                >
                  {/* Calendar with check SVG icon for scheduled date */}
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                    <path d="M9 16l2 2 4-4"></path>
                  </svg>
                </div>
                
                <div>
                  <div className="date-label">{t("inventoryDetails.scheduledDate")}</div>
                  <div className="date-value text-success">
                    {scheduledDate ? formatDate(scheduledDate) : "-"}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </Card.Body>
    </Card>
  );
};

export default DateInfoCard;
